/**
 * TTS abstraction. AudioOutput/KiaAssistantSDK depend only on this
 * interface, never on a concrete vendor — a real provider (browser-native
 * SpeechSynthesis today, a cloud provider later) is injected, so swapping
 * one out never touches the SDK core. The contract is:
 * final Gemini response text (+ its BCP-47 language) -> AudioOutput.speak(text, language).
 */
export interface AudioOutputProvider {
  /** `language` is a BCP-47 tag (e.g. "en-US", "hi-IN") for `text`, or null if none was available — picks the voice. */
  speak(text: string, language: string | null): Promise<void>;
  stop(): void;
  /** Releases anything the provider is holding onto — called once, on SDK destroy(). */
  dispose(): void;
  /**
   * Best-effort, synchronous "prime" hook — call from directly inside a real
   * user gesture (a click handler), before any `await`. Safari on iOS
   * silently refuses to play audio/speech that wasn't triggered by (or
   * closely tied to) a user gesture; this SDK's auto-play flow speaks the
   * response only after a full async round trip (record -> upload -> Gemini
   * -> TTS), well outside that window, so without this the first (and every)
   * reply would just play nothing on iOS, with no error at all. Optional —
   * a provider with nothing to prime (e.g. NoopAudioOutputProvider) can omit
   * it entirely.
   */
  unlock?(): void;
}

/**
 * Placeholder provider: does not synthesize or play any audio. Used as a
 * safe fallback and in tests — never depends on a real TTS backend/API key.
 */
export class NoopAudioOutputProvider implements AudioOutputProvider {
  async speak(_text: string, _language: string | null): Promise<void> {
    // Intentionally no-op.
  }

  stop(): void {
    // Intentionally no-op.
  }

  dispose(): void {
    // Intentionally no-op.
  }
}

export class AudioOutput {
  private speaking = false;

  constructor(private readonly provider: AudioOutputProvider = new NoopAudioOutputProvider()) {}

  isSpeaking(): boolean {
    return this.speaking;
  }

  async speak(text: string, language: string | null = null): Promise<void> {
    if (!text) {
      throw new Error('AudioOutput.speak() requires non-empty text.');
    }
    this.speaking = true;
    try {
      await this.provider.speak(text, language);
    } finally {
      this.speaking = false;
    }
  }

  stop(): void {
    this.provider.stop();
    this.speaking = false;
  }

  dispose(): void {
    this.provider.dispose();
    this.speaking = false;
  }

  /** See AudioOutputProvider.unlock() — call synchronously from within a real user gesture. */
  unlock(): void {
    this.provider.unlock?.();
  }
}
